// Thin native-enhancement layer for Capacitor.
// All functions degrade gracefully on web — safe to call from anywhere.
//
// Why this exists: Apple rejects PWA wrappers that "function identically to a
// website". Wiring even a few native APIs (haptics, native share, native push)
// is usually enough to clear review.

import { Capacitor } from '@capacitor/core';

export const isNativePlatform = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

export const milestoneHaptic = async () => {
  if (!isNativePlatform()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch { /* no-op */ }
};

export const checkHaptic = async () => {
  if (!isNativePlatform()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* no-op */ }
};

export const nativeShare = async ({ title, text, url, dialogTitle = 'Share your flow' }) => {
  if (!isNativePlatform()) return false;
  try {
    const { Share } = await import('@capacitor/share');
    const can = await Share.canShare();
    if (!can.value) return false;
    await Share.share({ title, text, url, dialogTitle });
    return true;
  } catch {
    return false;
  }
};

export const registerNativePush = async () => {
  if (!isNativePlatform()) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return null;
    await PushNotifications.register();
    return await new Promise((resolve) => {
      const sub = PushNotifications.addListener('registration', (t) => {
        sub.remove();
        resolve(t.value);
      });
    });
  } catch {
    return null;
  }
};

// Run a callback whenever the native app returns to the foreground. Useful
// for refreshing data shown on the home-screen widget. No-op on web.
export const onAppResume = async (cb) => {
  if (!isNativePlatform()) return () => {};
  try {
    const { App } = await import('@capacitor/app');
    const sub = await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) cb();
    });
    return () => { try { sub.remove(); } catch { /* no-op */ } };
  } catch {
    return () => {};
  }
};

export const initNativeUI = async () => {
  if (!isNativePlatform()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const isDark = document.documentElement.classList.contains('dark');
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    if (platform() === 'android') {
      await StatusBar.setBackgroundColor({ color: isDark ? '#121614' : '#F9F6F0' });
    }
  } catch { /* no-op */ }
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch { /* no-op */ }
};

// Reflect theme changes to the native status bar
export const syncStatusBarToTheme = async (isDark) => {
  if (!isNativePlatform()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    if (platform() === 'android') {
      await StatusBar.setBackgroundColor({ color: isDark ? '#121614' : '#F9F6F0' });
    }
  } catch { /* no-op */ }
};
